import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaChartLine, FaExchangeAlt, FaUniversity, FaHandshake } from 'react-icons/fa';

const FinancialServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Industries', path: '/industries' },
    { label: 'Financial Services' }
  ];

  const services = [
    {
      icon: <FaChartLine className="w-8 h-8 text-secondary" />,
      title: "Investment Banking",
      description: "Strategic advisory services for capital raising, mergers and acquisitions, and corporate restructuring for financial institutions."
    },
    {
      icon: <FaExchangeAlt className="w-8 h-8 text-secondary" />,
      title: "Asset Management",
      description: "Specialized investment strategies and portfolio management solutions for banks, insurance companies, and wealth management firms."
    },
    {
      icon: <FaUniversity className="w-8 h-8 text-secondary" />,
      title: "Banking Consulting",
      description: "Operational efficiency, digital transformation, and regulatory compliance advisory for banking and financial service organizations."
    },
    {
      icon: <FaHandshake className="w-8 h-8 text-secondary" />,
      title: "FinTech Solutions",
      description: "Strategic partnerships, growth strategies, and technology integration consulting for financial technology companies."
    }
  ];

  const clientTestimonials = [
    {
      quote: "Segway Capital's expertise in banking regulations and digital transformation has been invaluable to our institution. Their strategic guidance helped us modernize our systems while ensuring compliance.",
      author: "Jennifer Lawrence",
      position: "COO, Atlantic Financial Group"
    },
    {
      quote: "The asset management solutions provided by Segway Capital transformed our investment approach. Their deep understanding of market dynamics and risk management is exceptional.",
      author: "Mark Thompson",
      position: "CIO, Meridian Investment Bank"
    },
    {
      quote: "As a growing FinTech company, Segway Capital's strategic advice was crucial for our successful market expansion and capital acquisition. They truly understand the intersection of finance and technology.",
      author: "Sarah Kim",
      position: "CEO, NexGen Payment Solutions"
    }
  ];

  return (
    <>
      <PageHeader
        title="Financial Services"
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/person-looking-finance-graphs_52683-116611.jpg_mhsxez.jpg"
      />

      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
                Expert Financial Services Solutions
              </h2>
              <p className="text-lg text-gray-700">
                Segway Capital provides comprehensive advisory services to financial institutions, from global banks to emerging FinTech companies, helping them navigate regulatory challenges, optimize operations, and drive growth.
              </p>
              <p className="text-lg text-gray-700">
                Our team of experienced professionals brings deep industry knowledge and strategic insight to deliver tailored solutions that address the evolving needs of the financial services sector.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Explore Our Solutions
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/trader-using-smartphone-stock-market-report-buy-sell-forex-trading_220873-8114.jpg_m0uqkt.jpg" 
                alt="Financial Services" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Our Services for Financial Institutions
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We offer specialized consulting and advisory services tailored to the unique challenges and opportunities in the financial services industry.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {services.map((service, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{service.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3">{service.title}</h4>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Client Success Stories
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Learn how we've helped financial institutions achieve their strategic objectives.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {clientTestimonials.map((testimonial, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md"
              >
                <div className="text-4xl text-primary opacity-30 mb-4">"</div>
                <p className="text-gray-700 italic mb-6">{testimonial.quote}</p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-primary">{testimonial.author}</p>
                  <p className="text-sm text-gray-500">{testimonial.position}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="bg-primary text-white rounded-xl p-10 shadow-lg">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl md:text-3xl font-heading font-bold mb-4">Ready to Transform Your Financial Institution?</h3>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                Our financial services experts are ready to help you navigate challenges and seize opportunities in today's dynamic financial landscape.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Link 
                to="/contact-us" 
                className="px-8 py-3 bg-white text-primary hover:bg-gray-100 font-semibold rounded-lg transition-colors inline-block"
              >
                Schedule a Strategic Consultation
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FinancialServicesPage; 